import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Button, Table, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { businessService, Business, BusinessDirector, BusinessMember } from '../../services/businesses';
import customerApi from '../../services/customerApi';

interface BusinessTabProps {
  customerId?: number;
  isCustomerPortal?: boolean;
}

export const BusinessTab: React.FC<BusinessTabProps> = ({ customerId, isCustomerPortal = false }) => {
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (customerId || isCustomerPortal) {
      loadBusinesses();
    }
  }, [customerId, isCustomerPortal]);

  const loadBusinesses = async () => {
    setLoading(true);
    try {
      if (isCustomerPortal) {
        // Use customer API for customer portal
        const response = await customerApi.get('/customer/businesses');
        setBusinesses(response.data.data || []);
      } else if (customerId) {
        // Use admin API for admin portal
        const response = await businessService.getAll();
        // Filter businesses by directors/members customer_id
        const customerBusinesses = (response.data || []).filter((b: Business) =>
          (b.directors || []).some((director) => director.customer_id === customerId) ||
          (b.members || []).some((member) => member.customer_id === customerId)
        );
        setBusinesses(customerBusinesses);
      }
    } catch {
      message.error('Failed to load businesses');
    } finally {
      setLoading(false);
    }
  };

  // Combine all directors and members from all businesses
  const allDirectors: (BusinessDirector & { businessName: string })[] = [];
  const allMembers = useMemo(() => {
    const members: (BusinessMember & { businessName: string; totalShare: number; sharePercent?: number })[] = [];

    businesses.forEach((business) => {
      const businessMembers = business.members ?? [];
      if (businessMembers.length === 0) return;
      const totalShare = businessMembers.reduce((sum: number, member: any) => {
        const rawValue = member.share;
        const value =
          typeof rawValue === 'number'
            ? rawValue
            : typeof rawValue === 'string' && rawValue.trim() !== ''
              ? parseFloat(rawValue)
              : 0;
        return sum + value;
      }, 0);

      businessMembers
        .filter((member: any) => (customerId ? member.customer_id === customerId : true))
        .forEach((member: any) => {
          const rawShare = member.share;
          const shareValue =
            typeof rawShare === 'number'
              ? rawShare
              : typeof rawShare === 'string' && rawShare.trim() !== ''
                ? parseFloat(rawShare)
                : undefined;
          const sharePercent =
            shareValue && totalShare > 0 ? (shareValue / totalShare) * 100 : undefined;

          console.log('[Customer Business Share]', {
            business: business.name,
            customerId: member.customer_id,
            shareValue,
            totalShare,
            sharePercent: sharePercent ? `${sharePercent.toFixed(2)}%` : '-',
          });

          members.push({ ...member, businessName: business.name, totalShare, sharePercent });
        });
    });

    return members;
  }, [businesses, customerId]);

  businesses.forEach((business) => {
    const directors = business.directors ?? [];
    if (directors.length === 0) return;
    directors
      .filter((director: any) => (customerId ? director.customer_id === customerId : true))
      .forEach((director: any) => {
        allDirectors.push({ ...director, businessName: business.name });
      });
  });

  const directorColumns = [
    {
      title: 'Business',
      dataIndex: 'businessName',
      key: 'businessName',
      render: (text: string, record: BusinessDirector & { businessName: string }) => {
        const businessId = record.business_id;
        if (!businessId) return text || '-';
        const basePath = isCustomerPortal ? '/customer' : '';
        return (
          <Button
            type="link"
            size="small"
            style={{ padding: 0 }}
            onClick={() => navigate(`${basePath}/businesses/${businessId}/edit`)}
          >
            {text || '-'}
          </Button>
        );
      },
    },
    {
      title: 'Position',
      dataIndex: 'position',
      key: 'position',
      render: (text: string) => text || '-',
    },
    {
      title: 'Appoint Date',
      dataIndex: 'appoint_date',
      key: 'appoint_date',
      render: (date: string) => (date ? dayjs(date).format('DD/MM/YYYY') : '-'),
    },
    {
      title: 'Resign Date',
      dataIndex: 'resign_date',
      key: 'resign_date',
      render: (date: string) => (date ? dayjs(date).format('DD/MM/YYYY') : '-'),
    },
  ];

  const memberColumns = [
    {
      title: 'Business',
      dataIndex: 'businessName',
      key: 'businessName',
      render: (text: string, record: BusinessMember & { businessName: string }) => {
        const businessId = record.business_id;
        if (!businessId) return text || '-';
        const basePath = isCustomerPortal ? '/customer' : '';
        return (
          <Button
            type="link"
            size="small"
            style={{ padding: 0 }}
            onClick={() => navigate(`${basePath}/businesses/${businessId}/edit`)}
          >
            {text || '-'}
          </Button>
        );
      },
    },
    {
      title: 'Share',
      dataIndex: 'share',
      key: 'share',
      render: (_: number, record: BusinessMember & { sharePercent?: number }) => {
        if (!record.sharePercent) return '-';
        return `${record.sharePercent.toFixed(2)}%`;
      },
    },
    {
      title: 'Appoint Date',
      dataIndex: 'appoint_date',
      key: 'appoint_date',
      render: (date: string) => (date ? dayjs(date).format('DD/MM/YYYY') : '-'),
    },
    {
      title: 'Resign Date',
      dataIndex: 'resign_date',
      key: 'resign_date',
      render: (date: string) => (date ? dayjs(date).format('DD/MM/YYYY') : '-'),
    },
  ];

  return (
    <div>
      <div style={{ fontWeight: 600, marginBottom: 8 }}>Directors</div>
      <Table
        columns={directorColumns}
        dataSource={allDirectors.map((d, index) => ({ ...d, key: `director-${index}` }))}
        pagination={false}
        size="small"
        loading={loading}
      />

      <div style={{ fontWeight: 600, margin: '16px 0 8px' }}>Members</div>
      <Table
        style={{ marginTop: 16 }}
        columns={memberColumns}
        dataSource={allMembers.map((m, index) => ({ ...m, key: `member-${index}` }))}
        pagination={false}
        size="small"
        loading={loading}
      />
    </div>
  );
};
